import { useEffect, useState } from 'react'
import { Bell } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { api } from '../../services/api'
import { RootState } from '../../store/store'
import { openFoodLogMissWhatsapp } from '../../utils/openFoodLogMissWa'

type AdminNote = {
  id: string
  type: string
  title: string
  body: string
  href: string
  readAt: string | null
  createdAt: string
  hasPhone?: boolean
  waUrl?: string | null
}

const TYPE_LABEL: Record<string, string> = {
  new_user: 'Alta',
  note: 'Nota',
  measurement: 'Medidas',
  plan_expiry: 'Plan',
  food_log_miss: 'Recordatorio',
}

function formatWhen(iso: string) {
  try {
    return new Intl.DateTimeFormat('es-CO', {
      timeZone: 'America/Bogota',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return String(iso || '').slice(0, 16)
  }
}

export default function AdminNotificationsPage() {
  const role = useSelector((s: RootState) => s.auth.user?.role)
  const [items, setItems] = useState<AdminNote[]>([])
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  const load = () => {
    setLoading(true)
    // list ya hace ensure post-15:00. Si sigue vacío (tabla Aurora recién creada / cron fallido),
    // un status force recupera y se vuelve a listar una sola vez.
    api.get('/admin-notifications', { params: { limit: 100 } })
      .then(async (r) => {
        let rows = Array.isArray(r.data) ? r.data : []
        if (rows.length === 0) {
          await api.get('/admin-notifications/food-log-miss-status').catch(() => null)
          const again = await api.get('/admin-notifications', { params: { limit: 100 } }).catch(() => null)
          rows = Array.isArray(again?.data) ? again.data : []
        }
        setItems(rows)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  const open = async (n: AdminNote) => {
    if (n.type === 'food_log_miss') {
      await openFoodLogMissWhatsapp(n.id, n.waUrl)
      load()
      return
    }
    if (!n.readAt) await api.patch(`/admin-notifications/${n.id}/read`).catch(() => {})
    navigate(role === 'TRAINER' && (!n.href || String(n.href).startsWith('/admin/users'))
      ? '/admin/vigencias'
      : (n.href || '/admin/users'))
    load()
  }

  const markAll = async () => {
    await api.post('/admin-notifications/read-all').catch(() => {})
    load()
  }

  const unread = items.filter((n) => !n.readAt).length

  return (
    <div className="p-6 max-w-2xl mx-auto">
      <div className="flex items-start justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-[#64ba30]/10 flex items-center justify-center">
            <Bell size={20} className="text-[#64ba30]" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-[#111827]">Historial de notificaciones</h1>
            <p className="text-sm text-gray-500">
              {role === 'TRAINER'
                ? 'Avisos de tus asesorados. Desde las 15:00 (Bogotá), si no cargaron alimentos hoy aparece un recordatorio. Al tocarlo se abre WhatsApp con el mensaje listo (si tienen teléfono).'
                : 'Altas, notas, medidas, vencimientos y registro de alimentos. Al abrir un aviso queda leído.'}
            </p>
          </div>
        </div>
        {unread > 0 && (
          <button type="button" onClick={markAll} className="text-xs font-semibold text-[#64ba30] whitespace-nowrap">
            Marcar leídas ({unread})
          </button>
        )}
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {loading ? (
          <p className="px-4 py-12 text-center text-sm text-gray-500">Cargando historial…</p>
        ) : items.length === 0 ? (
          <div className="px-4 py-12 text-center">
            <p className="text-sm text-gray-600 font-medium">Aún no hay avisos en el historial</p>
            <p className="text-xs text-gray-500 mt-2 max-w-sm mx-auto">
              Desde las 15:00 (Bogotá) se crea un aviso por asesorado que no registró alimentos hoy. Al recargar se completan los que falten. Sin teléfono no se puede abrir WhatsApp.
            </p>
          </div>
        ) : items.map((n) => (
          <button
            key={n.id}
            type="button"
            onClick={() => open(n)}
            className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-[#64ba30]/5 ${n.readAt ? '' : 'bg-[#64ba30]/10'}`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[10px] font-semibold uppercase tracking-wide text-[#64ba30]">
                {TYPE_LABEL[n.type] || n.type}
              </span>
              <span className={`text-[10px] font-semibold ${n.readAt ? 'text-gray-400' : 'text-[#64ba30]'}`}>
                {n.readAt ? 'Leída' : 'Sin leer'}
              </span>
            </div>
            <p className="text-sm font-medium text-[#111827]">{n.title}</p>
            {n.body ? <p className="text-xs text-gray-500 mt-0.5">{n.body}</p> : null}
            {n.type === 'food_log_miss' && (
              <p className={`text-[11px] mt-1 font-medium ${n.hasPhone || n.waUrl ? 'text-[#64ba30]' : 'text-amber-600'}`}>
                {n.hasPhone || n.waUrl ? 'Abrir WhatsApp' : 'Sin teléfono — no se puede abrir WhatsApp'}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-1">{formatWhen(n.createdAt)}</p>
          </button>
        ))}
      </div>
    </div>
  )
}
